import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Line from "../../component/ui/Line";

gsap.registerPlugin(ScrollTrigger);

const VisionMissionSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      // --- 1. Scrubbed Text Reveal Animation ---
      // Select all text elements with the specific class
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content")
      );

      textElements.forEach((element, index) => {
        // Set Initial State
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest(".text-wrapper"), // Changed to target the specific wrapper
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });

        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
        });
      });

      // --- 2. Scrubbed Divider Line Animation ---
      // Updated to handle multiple lines (Vision line AND Mission line)
      const lineElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".anim-line")
      );

      lineElements.forEach((line) => {
        // Initial State
        gsap.set(line, { scaleX: 0, transformOrigin: "left" });

        gsap.to(line, {
          scaleX: 1,
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: line.closest(".line-wrapper"), // Trigger based on the line's container
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full mt-[40px] 2xl:mt-[80px] font-[Sora]"
    >
      <div className="max-w-[1612px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-[40px] 2xl:gap-[80px] justify-start items-center w-full">
          {/* ================= Vision Section ================= */}
          <div className="flex flex-col lg:flex-row justify-start items-center w-full gap-8 lg:gap-[72px]">
            <div className="flex flex-col gap-[11px] 2xl:gap-[22px] justify-start items-start w-full lg:w-1/2">
              {/* Label & Line */}
              <div className="flex flex-row justify-start items-center w-full mb-[14px] 2xl:mb-[28px] line-wrapper overflow-hidden">
                {/* Wrapped text for clean animation */}
                <div className="text-wrapper overflow-hidden">
                  <span className="block text-[16px] 2xl:text-[24px] font-normal leading-xl text-left text-text-primary animated-text-content">
                    Vision
                  </span>
                </div>
                {/* Added 'anim-line' class */}
                <div className="anim-line ml-[15px] 2xl:ml-[30px]">
                  <Line
                    className="bg-text-primary"
                    width="100px"
                    height="2px"
                  />
                </div>
              </div>

              {/* Heading */}
              <div className="text-wrapper overflow-hidden w-full">
                <h2 className="block text-[30px] 2xl:text-[60px] font-bold  text-left text-text-primary mb-[5px] 2xl:mb-[19px] animated-text-content">
                  Our Vision
                </h2>
              </div>

              {/* Paragraph */}
              <div className="text-wrapper overflow-hidden w-full">
                <p className="block text-[16px] 2xl:text-[24px] font-normal leading-lg text-justify text-text-primary animated-text-content">
                  To be the leading force in sustainable energy and organic
                  agriculture, pioneering a global movement where organic waste
                  is fully transformed into valuable resources. Our vision is to
                  empower communities, ensure environmental well-being, and
                  secure a cleaner, greener, and energy-independent future for
                  generations to come.
                </p>
              </div>
            </div>

            {/* Image (Standard - no complex animation applied as per request, keeps focus on text) */}
            <div className="relative w-full lg:w-[48%] flex justify-center">
              <div className="relative">
                <img
                  src="/images/aboutImg/visionPlant.png"
                  alt="Vision image"
                  className="inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* ================= Mission Section ================= */}
          <div className="flex flex-col-reverse lg:flex-row justify-between items-center w-full gap-8 lg:gap-[72px]">
            <div className="relative w-full lg:w-[48%] flex justify-center">
              <div className="relative">
                <img
                  src="/images/aboutImg/misionFek.webp"
                  alt="Mission image"
                  className="inset-0 w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex flex-col gap-[11px] 2xl:gap-[22px] justify-start items-start w-full lg:w-1/2">
              {/* Label & Line */}
              <div className="flex flex-row justify-start items-center w-full mb-[14px] 2xl:mb-[28px] line-wrapper overflow-hidden">
                <div className="text-wrapper overflow-hidden">
                  <span className="block text-[16px] 2xl:text-[24px] font-normal leading-xl text-left text-text-primary animated-text-content">
                    Mission
                  </span>
                </div>
                <div className="anim-line ml-[15px] 2xl:ml-[30px]">
                  <Line
                    className="bg-text-primary"
                    width="100px"
                    height="2px"
                  />
                </div>
              </div>

              {/* Heading */}
              <div className="text-wrapper overflow-hidden w-full">
                <h2 className="block text-[30px] 2xl:text-[60px] font-bold  text-left text-text-primary mb-[5px] 2xl:mb-[19px] animated-text-content">
                  Our Mission
                </h2>
              </div>

              {/* Paragraph */}
              <div className="text-wrapper overflow-hidden w-full">
                <p className="block text-[16px] 2xl:text-[24px] font-bold leading-lg text-justify text-text-primary animated-text-content">
                  Securing Tomorrow's Resources Today
                </p>
                <p className="block text-[16px] 2xl:text-[24px] font-normal leading-lg text-justify text-text-primary animated-text-content">
                  At Govardhannathji Energies LLP, our purpose is to bridge the
                  gap between waste management, energy demand, and sustainable
                  agriculture. We operate on the fundamental principle of the
                  circular economy: nothing is wasted, everything is valued. Our
                  advanced biogas facilities deliver Guaranteed Energy,
                  Responsibly Sourced.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionMissionSection;
